/**
 * `useAgents(projectId)` - TanStack Query read hook for the agents that belong
 * to a single project.
 *
 * Implements the AC-1407 / AC-1377 P4.1 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4 (key
 * conventions), 4 (Phase 4 project resources), and 5.2 (read-hook signatures).
 *
 * ## Why this exists
 *
 * Today, `AgentsPage`, `AgentFormModal`, `ToolManagementSystem`, and
 * `Dashboard` read agents through `useProjectStore` (`agents`, `loadAgents`).
 * That path has no caching, no request deduplication, and forces every
 * mutation to manually re-call `loadAgents(projectId)` to refresh consumers.
 * The class of bugs this caused is documented in AC-1390 / AC-1391 / AC-1394
 * (stale-modal-data) and is exactly what the AC-1377 RQ migration closes for
 * workflows; this ticket extends the same pattern to project-scoped agents.
 *
 * During Phase 4 the hook MUST coexist with `useProjectStore.agents` /
 * `loadAgents`. AC-1411 migrates the consumers (AgentsPage, AgentFormModal,
 * ToolManagementSystem, Dashboard); AC-1412 deletes the project-store slices
 * once nothing reads them. Until then, the two paths can subscribe in
 * parallel - RQ's per-key request dedup makes the duplicate fetch cheap and
 * the cache slots are independent.
 *
 * ## Behavior
 *
 * - Fetches `apiService.getAgents(projectId)` with cache key
 *   `queryKeys.projectAgents(projectId)` (the AC-1396 factory; resolves to
 *   `['project', String(projectId), 'agents']`). The factory coerces `'7'`
 *   and `7` to the same array shape, so route-string IDs and store-numeric
 *   IDs share a single cache slot. This is the AC-1396 coercion contract.
 *
 * - `enabled` is auto-disabled for empty IDs (`undefined`, `null`, `''`).
 *   Same rationale as `useWorkflow` / `useWorkflowSteps` - without this
 *   guard, a `useQuery` whose key includes `String(undefined)` would cache
 *   under a useless key AND fire an HTTP request for an unknown project.
 *   Today's `loadAgents` call sites all guard with `if (!currentProject)`
 *   in a `useEffect`; this hook bakes that guard in.
 *
 * - Caller-provided `options` flow through to `useQuery`. Callers can
 *   override `staleTime`, `enabled`, `refetchOnMount`, etc. per-call without
 *   forking the hook. The caller's `enabled` is AND'ed with the internal
 *   id-truthy guard, so passing `{ enabled: false }` always wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` - those are owned by
 *   the hook (the whole point of centralizing the key factory is to prevent
 *   call-site key drift). The `Options` type below excludes them.
 *
 * ## Argument forwarded to `apiService.getAgents`
 *
 * The `projectId` value is passed through to `apiService.getAgents` as a
 * string once the truthy-id guard has cleared. `getAgents` URL-interpolates
 * its argument (`/api/v1/projects/${projectId}/agents`); we coerce to string
 * to match the apiService signature directly. The cache key factory uses
 * `String(projectId)` independently, so both forms produce the same key.
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { Agent } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook - letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseAgentsOptions = Omit<
  UseQueryOptions<Agent[], Error, Agent[], ReturnType<typeof queryKeys.projectAgents>>,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<Agent[]>` from TanStack Query.
 *
 * @param projectId Project ID. May be `string`, `number`, `undefined`, or
 *                  `null`. Falsy values disable the query (no fetch fires).
 * @param options   Optional `useQuery` options. `queryKey` and `queryFn` are
 *                  excluded from this type because they are owned by the hook.
 *
 * @example
 *   const { data: agents, isLoading } = useAgents(projectId);
 *   if (isLoading) return <Spinner />;
 *   return <AgentList agents={agents ?? []} />;
 *
 * @example
 *   // Defer the fetch until a project is selected.
 *   const project = useCurrentProject();
 *   const agents  = useAgents(project?.project_id);
 */
export function useAgents(
  projectId: string | number | undefined | null,
  options?: UseAgentsOptions,
): UseQueryResult<Agent[], Error> {
  // Treat `undefined`, `null`, and `''` as "no project selected yet".
  const hasId = projectId !== undefined && projectId !== null && projectId !== '';

  // The caller's `enabled` (if provided) is AND'ed with our internal
  // truthy-id guard. Either being falsy keeps the query disabled.
  const callerEnabled = options?.enabled;
  const enabled = hasId && (callerEnabled === undefined ? true : callerEnabled);

  return useQuery<Agent[], Error, Agent[], ReturnType<typeof queryKeys.projectAgents>>({
    // Placeholder ID when `hasId` is false: RQ requires queryKey
    // unconditionally, but `enabled: false` guarantees the queryFn never
    // runs and the placeholder slot is never populated.
    queryKey: queryKeys.projectAgents(hasId ? (projectId as string | number) : 0),
    // Forward as string to match the apiService signature. `getAgents`
    // URL-interpolates so any string-coercible value works; we use
    // `String(...)` here so the API call shape matches the cache key
    // factory's `String(...)` coercion.
    queryFn: () => apiService.getAgents(String(projectId)),
    ...options,
    enabled,
  });
}

export default useAgents;
